import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Lock, LogOut } from "lucide-react";
import LeadsAdmin from "@/components/admin/LeadsAdmin";
import PropertiesAdmin from "@/components/admin/PropertiesAdmin";

const STORAGE_KEY = "leos_admin_pw";

const TABS = [
    { id: "leads", label: "Leads" },
    { id: "properties", label: "Properties" },
];

export default function Admin() {
    const [pw, setPw] = useState("");
    const [authed, setAuthed] = useState(false);
    const [tab, setTab] = useState("leads");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) verify(saved);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verify = async (password) => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/admin/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });
            if (!r.ok) {
                toast.error("Wrong password.");
                sessionStorage.removeItem(STORAGE_KEY);
                setAuthed(false);
                return;
            }
            sessionStorage.setItem(STORAGE_KEY, password);
            setPw(password);
            setAuthed(true);
        } catch {
            toast.error("Couldn't connect.");
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        verify(pw);
    };

    const logout = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
        setPw("");
    };

    if (!authed) {
        return (
            <div className="min-h-screen bg-cream pt-32 pb-20 flex items-center justify-center">
                <div className="w-full max-w-md mx-auto px-6">
                    <div className="text-center mb-10">
                        <div className="inline-flex w-14 h-14 border border-gold/40 items-center justify-center mb-5">
                            <Lock size={20} className="text-gold" strokeWidth={1.2} />
                        </div>
                        <div className="eyebrow mb-4">Admin Access</div>
                        <h1 className="headline text-4xl text-ink mb-3">Sign in</h1>
                        <p className="text-ink-soft font-light text-sm">
                            Enter the admin password to manage leads &amp; properties.
                        </p>
                    </div>
                    <form onSubmit={onSubmit} className="bg-bone border border-line-soft p-8">
                        <label className="eyebrow text-ink-soft block mb-2">Password</label>
                        <input
                            type="password"
                            autoFocus
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            className="input-line mb-6"
                            data-testid="admin-password-input"
                            placeholder="••••••••"
                        />
                        <button
                            type="submit"
                            disabled={loading || !pw}
                            className="btn-gold w-full disabled:opacity-60"
                            data-testid="admin-login-btn"
                        >
                            {loading ? "Verifying..." : "Sign in"}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream pt-32 pb-24">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div className="eyebrow mb-3">Admin Dashboard</div>
                        <h1 className="headline text-4xl md:text-5xl text-ink">
                            Leo&rsquo;s Luxury Control Centre
                        </h1>
                    </div>
                    <button
                        onClick={logout}
                        className="btn-ghost inline-flex items-center gap-2 self-start md:self-auto"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>

                {/* Tabs */}
                <div className="border-b border-line-soft mb-10 flex gap-1">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-5 md:px-7 py-3 text-[12px] uppercase tracking-[0.22em] transition-colors border-b-2 -mb-px ${
                                tab === t.id
                                    ? "border-gold text-gold"
                                    : "border-transparent text-ink-soft hover:text-ink"
                            }`}
                            data-testid={`admin-tab-${t.id}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "leads" && <LeadsAdmin password={pw} />}
                {tab === "properties" && <PropertiesAdmin password={pw} />}
            </div>
        </div>
    );
}
